"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useAuth } from "@/lib/auth"
import { Loader2, AlertTriangle, MapPin } from "lucide-react"
import { useCreateReport } from "@/hooks/useCreateReport"
import { useToast } from "@/hooks/use-toast"
import type { FrontendReportType, FrontendPriority } from "@/utils/report-mappers"
import { getCampusLocations } from "@/services/campus-locations.service"
import type { Database } from "@/types/database.types"

type CampusLocation = Database['public']['Tables']['campus_locations']['Row']

interface CreateReportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function CreateReportDialog({ open, onOpenChange, onSuccess }: CreateReportDialogProps) {
  const { user } = useAuth()
  const { toast } = useToast()
  const { createNewReport, isCreating, error, success, reset } = useCreateReport()
  const [campusLocations, setCampusLocations] = useState<CampusLocation[]>([])
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    type: "" as FrontendReportType | "",
    description: "",
    locationId: "",
    priority: "media" as FrontendPriority,
    anonymous: false,
  })

  // Cargar ubicaciones del campus cuando se abre el diálogo
  useEffect(() => {
    if (open && campusLocations.length === 0) {
      setLoadingLocations(true)
      getCampusLocations()
        .then((locations) => setCampusLocations(locations))
        .catch((err) => {
          console.error('Error cargando ubicaciones:', err)
          toast({
            title: "Error al cargar ubicaciones",
            description: "No se pudieron cargar las ubicaciones del campus.",
            variant: "destructive",
          })
        })
        .finally(() => setLoadingLocations(false))
    }
  }, [open, campusLocations.length, toast])

  // Efecto para manejar el éxito de la creación
  useEffect(() => {
    if (success) {
      toast({
        title: "Reporte creado exitosamente",
        description: "Tu reporte ha sido enviado y será revisado por el equipo de seguridad.",
      })

      // Reset form
      setFormData({
        title: "",
        type: "",
        description: "",
        locationId: "",
        priority: "media",
        anonymous: false,
      })

      // Llamar callback de éxito para refrescar lista
      onSuccess?.()

      // Cerrar diálogo
      onOpenChange(false)

      // Reset estado del hook
      reset()
    }
  }, [success, onSuccess, onOpenChange, toast, reset])

  // Efecto para manejar errores
  useEffect(() => {
    if (error) {
      toast({
        title: "Error al crear reporte",
        description: error.message || "Ocurrió un error al enviar tu reporte. Por favor intenta nuevamente.",
        variant: "destructive",
      })
    }
  }, [error, toast])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validar que el tipo esté seleccionado
    if (!formData.type) {
      toast({
        title: "Tipo de incidente requerido",
        description: "Por favor selecciona el tipo de incidente.",
        variant: "destructive",
      })
      return
    }

    // Validar que la ubicación esté seleccionada
    if (!formData.locationId) {
      toast({
        title: "Ubicación requerida",
        description: "Por favor selecciona la ubicación del incidente.",
        variant: "destructive",
      })
      return
    }

    try {
      await createNewReport({
        title: formData.title,
        type: formData.type,
        description: formData.description,
        locationId: formData.locationId,
        priority: formData.priority,
        anonymous: formData.anonymous,
      })
    } catch (err) {
      // El error ya se maneja en el useEffect
      console.error('Error en handleSubmit:', err)
    }
  }

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Agrupar ubicaciones por tipo
  const groupedLocations = campusLocations.reduce((acc, location) => {
    const type = location.type || 'Otros'
    if (!acc[type]) {
      acc[type] = []
    }
    acc[type].push(location)
    return acc
  }, {} as Record<string, CampusLocation[]>)

  // Obtener la ubicación seleccionada para mostrar su nombre
  const selectedLocation = campusLocations.find(loc => loc.id === formData.locationId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            <span>Crear Nuevo Reporte</span>
          </DialogTitle>
          <DialogDescription>Reporta incidentes de seguridad para mantener seguro nuestro campus</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">
              Título del Reporte <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              placeholder="Ej: Robo de mochila"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">
              Tipo de Incidente <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.type} onValueChange={(value) => handleChange("type", value)} required>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="robo">Robo</SelectItem>
                <SelectItem value="vandalismo">Vandalismo</SelectItem>
                <SelectItem value="sospechoso">Persona Sospechosa</SelectItem>
                <SelectItem value="emergencia">Emergencia</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="locationId">
              Ubicación del Incidente <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.locationId}
              onValueChange={(value) => handleChange("locationId", value)}
              required
              disabled={loadingLocations}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingLocations ? "Cargando ubicaciones..." : "Selecciona la ubicación"}>
                  {selectedLocation && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedLocation.name}</span>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {Object.entries(groupedLocations).map(([type, locations]) => (
                  <div key={type}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                      {type}
                    </div>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{location.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Prioridad</Label>
            <Select value={formData.priority} onValueChange={(value) => handleChange("priority", value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="baja">Baja</SelectItem>
                <SelectItem value="media">Media</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Descripción Detallada <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Describe lo que pasó, cuándo, y cualquier detalle relevante..."
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="anonymous"
              checked={formData.anonymous}
              onChange={(e) => handleChange("anonymous", e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="anonymous" className="text-sm">
              Reportar de forma anónima
            </Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar Reporte"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
